import {
  deleteV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdResourceScopesId,
  deleteV1CompaniesCompanyIdResourceScopesId,
  getV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdUsersUserIdResourceScopes,
  getV1CompaniesCompanyIdUsersUserIdResourceScopes,
  postV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdUsersUserIdResourceScopes,
  PostV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdUsersUserIdResourceScopesData,
  postV1CompaniesCompanyIdUsersUserIdResourceScopes,
  PostV1CompaniesCompanyIdUsersUserIdResourceScopesData,
  ResourceType,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';

import { SitesDiff } from '~components/UserForm/UserForm';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { fetchAllApiPages } from '~utils/fetchAllPages';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { ResourceScope } from './models';

interface CreateResourceScopeParams {
  userId: string;
  resourceId: string;
  resourceType: ResourceType;
  companyId?: string;
  managedCompanyId?: string;
}

interface DeleteResourceScopeParams {
  resourceId: string;
  companyId?: string;
  managedCompanyId?: string;
}

interface GetAllResourceScopeParams {
  userId: string;
  resourceType: ResourceType;
  companyId?: string;
  managedCompanyId?: string;
}

export const useResourceScopes = () => {
  const { userStore } = useStores();
  const effectiveCompanyId = getEffectiveUserCompanyId(userStore);

  const createResourceScope = async ({
    userId,
    resourceId,
    resourceType,
    companyId,
    managedCompanyId,
  }: CreateResourceScopeParams) => {
    try {
      if (companyId && managedCompanyId) {
        const body: PostV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdUsersUserIdResourceScopesData['body'] =
          {
            resource_id: resourceId,
            resource_type: resourceType,
          };
        const response =
          await postV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdUsersUserIdResourceScopes(
            {
              path: {
                'company-id': companyId,
                'managed-company-id': managedCompanyId,
                'user-id': userId,
              },
              body,
            },
          );
        return response;
      } else {
        const body: PostV1CompaniesCompanyIdUsersUserIdResourceScopesData['body'] = {
          resource_id: resourceId,
          resource_type: resourceType,
        };
        const response = await postV1CompaniesCompanyIdUsersUserIdResourceScopes({
          path: {
            'company-id': companyId || effectiveCompanyId,
            'user-id': userId,
          },
          body,
        });
        return response;
      }
    } catch (e) {
      connection.handleRequestError(
        e,
        t('error_messages.resource_scopes.failed_to_create'),
      );
    }
  };

  const deleteResourceScope = async ({
    resourceId,
    companyId,
    managedCompanyId,
  }: DeleteResourceScopeParams) => {
    try {
      if (companyId && managedCompanyId) {
        await deleteV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdResourceScopesId({
          path: {
            'company-id': companyId,
            'managed-company-id': managedCompanyId,
            id: resourceId,
          },
        });
      } else {
        await deleteV1CompaniesCompanyIdResourceScopesId({
          path: {
            'company-id': companyId || effectiveCompanyId,
            id: resourceId,
          },
        });
      }
    } catch (e) {
      connection.handleRequestError(
        e,
        t('error_messages.resource_scopes.failed_to_delete'),
      );
    }
  };

  const getAllResourceScopes = async ({
    userId,
    resourceType,
    companyId,
    managedCompanyId,
  }: GetAllResourceScopeParams) => {
    try {
      return await fetchAllApiPages(
        (query) =>
          companyId && managedCompanyId
            ? getV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdUsersUserIdResourceScopes(
                {
                  path: {
                    'company-id': companyId,
                    'managed-company-id': managedCompanyId,
                    'user-id': userId,
                  },
                  query: { 'filter[resource_type]': resourceType, ...query },
                },
              )
            : getV1CompaniesCompanyIdUsersUserIdResourceScopes({
                path: {
                  'company-id': companyId || effectiveCompanyId,
                  'user-id': userId,
                },
                query: { 'filter[resource_type]': resourceType, ...query },
              }),
        ResourceScope.parse,
      );
    } catch (e) {
      return connection.handleRequestError(
        e,
        t('error_messages.resource_scopes.failed_to_fetch'),
      );
    }
  };

  const saveUserSitesDiff = async (
    userId: string,
    sitesDiff: SitesDiff,
    companyId?: string,
    managedCompanyId?: string,
  ) => {
    const { siteIdsToAdd, resourceIdsToDelete } = sitesDiff;

    const createSiteRequests = siteIdsToAdd.map((siteId) => {
      return createResourceScope({
        userId,
        resourceId: siteId,
        resourceType: ResourceType.SITE,
        companyId,
        managedCompanyId,
      });
    });

    const deleteSiteRequests = resourceIdsToDelete.map((resourceId) => {
      return deleteResourceScope({ resourceId, companyId, managedCompanyId });
    });

    await Promise.all([...createSiteRequests, ...deleteSiteRequests]);
  };

  return {
    getAllResourceScopes,
    saveUserSitesDiff,
  };
};
